import React, { useState } from 'react';
import { Client, CompanyProfile } from '../types';
import { formatLocalDate, normalizeString } from '../utils/storage';
import { jsPDF } from 'jspdf';
import { 
  Plus, 
  Search, 
  Edit, 
  FileDown, 
  X, 
  UserPlus, 
  Filter, 
  Check, 
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  ShieldAlert
} from 'lucide-react';

interface ClientsProps {
  clients: Client[];
  profile: CompanyProfile;
  onAddClient: (c: Omit<Client, 'id' | 'createdAt'>) => void;
  onUpdateClient: (id: string, c: Partial<Client>) => void;
  isAdmin: boolean;
}

export default function Clients({ clients, profile, onAddClient, onUpdateClient, isAdmin }: ClientsProps) {
  // Filters & State
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | 'ativos' | 'inativos'>('todos');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [document, setDocument] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState('Rio de Janeiro');
  const [city, setCity] = useState('');

  // Report Modal States
  const [reportAllTime, setReportAllTime] = useState(true);
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [reportStatus, setReportStatus] = useState<'todos' | 'ativos' | 'inativos'>('todos');

  // Search logic
  const filteredClients = clients.filter(c => {
    const normalizedSearch = normalizeString(search);
    const searchDigits = search.replace(/\D/g, ''); // Get digits from search input
    
    // Normalize client data
    const safeNameNormalized = normalizeString(c.name || '');
    const safeDocDigits = (c.document || '').replace(/\D/g, '');
    const safePhoneDigits = (c.phone || '').replace(/\D/g, '');
    const safeEmailNormalized = normalizeString(c.email || '');

    // Search conditions
    const matchesName = safeNameNormalized.includes(normalizedSearch);
    const matchesDoc = searchDigits.length > 0 && safeDocDigits.includes(searchDigits);
    const matchesPhone = searchDigits.length > 0 && safePhoneDigits.includes(searchDigits);
    const matchesEmail = safeEmailNormalized.includes(normalizedSearch);
    
    const matchesSearch = matchesName || matchesDoc || matchesPhone || matchesEmail;
    
    if (statusFilter === 'ativos') return matchesSearch && c.active;
    if (statusFilter === 'inativos') return matchesSearch && !c.active;
    return matchesSearch;
  });

  const openAddModal = () => {
    setName('');
    setBirthDate('');
    setDocument('');
    setPhone('');
    setEmail('');
    setState('Rio de Janeiro');
    setCity('');
    setShowAddModal(true);
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAddClient({
      name,
      birthDate,
      document,
      phone,
      email,
      state,
      city,
      active: true
    });
    setShowAddModal(false);
  };

  const openEditModal = (client: Client) => {
    setSelectedClient(client);
    setName(client.name);
    setBirthDate(client.birthDate);
    setDocument(client.document);
    setPhone(client.phone);
    setEmail(client.email);
    setState(client.state);
    setCity(client.city);
    setShowEditModal(true);
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) return;
    onUpdateClient(selectedClient.id, {
      name,
      birthDate,
      document,
      phone,
      email,
      state,
      city
    });
    setShowEditModal(false);
  };

  // PDF Report generator
  const generatePDFReport = () => {
    try {
      const doc = new jsPDF();
      
      // Filter clients for report
      const rClients = clients.filter(c => {
        // Status filter
        if (reportStatus === 'ativos' && !c.active) return false;
        if (reportStatus === 'inativos' && c.active) return false;

        // Date filter
        if (!reportAllTime) {
          if (reportStart && c.createdAt < reportStart) return false;
          if (reportEnd && c.createdAt > reportEnd) return false;
        }
        return true;
      });

      // Top Header Dark Bar
      doc.setFillColor(24, 24, 27); // Zinc-900
      doc.rect(0, 0, 210, 42, 'F');
      
      // Pousada Identity Left Side
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(profile.name || "Pousada Recanto do Sol", 15, 14);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(212, 212, 216); // Zinc-300
      const corpName = profile.corporateName || "F. M. Dos Santos Hospedagens LTDA";
      const cnpj = profile.document ? `CNPJ: ${profile.document}` : '';
      doc.text(`${corpName} ${cnpj ? `| ${cnpj}` : ''}`, 15, 20);
      doc.text(`Endereço: ${profile.address || "Rua das Conchas, 145 - Centro, Búzios - RJ"}`, 15, 26);
      doc.text(`Tel: ${profile.phone} | Email: ${profile.email}`, 15, 32);
      
      // Title Block Right Side
      doc.setFillColor(39, 39, 42); // Zinc-800
      doc.rect(142, 8, 54, 26, 'F');
      doc.setDrawColor(63, 63, 70); // Zinc-700
      doc.rect(142, 8, 54, 26, 'D');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.text("RELATÓRIO OPERACIONAL", 169, 14, { align: 'center' });
      doc.text("CATÁLOGO DE CLIENTES", 169, 18, { align: 'center' });
      
      doc.setFontSize(9);
      doc.text(reportStatus.toUpperCase(), 169, 26, { align: 'center' });
      doc.setFontSize(6.5);
      doc.setTextColor(161, 161, 170);
      doc.text(reportAllTime ? "PERÍODO: COMPLETO" : `PERÍODO: DE ${formatLocalDate(reportStart)} A ${formatLocalDate(reportEnd)}`, 169, 31, { align: 'center' });

      // Reset
      doc.setTextColor(24, 24, 27); // Zinc-900
      
      let currentY = 54;
      
      // Summary Counts Cards
      doc.setFillColor(244, 244, 245); // Zinc-100
      doc.rect(15, currentY, 180, 12, 'F');
      
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(63, 63, 70);
      doc.text(`RESULTADO DA PESQUISA: DEVOLVEU ${rClients.length} CADASTRO(S)`, 20, currentY + 8);
      
      currentY += 20;

      // Draw table header
      doc.setFillColor(244, 244, 245);
      doc.rect(15, currentY, 180, 8, 'F');
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(39, 39, 42);
      
      doc.text("NOME DO HÓSPEDE", 18, currentY + 5.5);
      doc.text("CPF / DOCUMENTO", 75, currentY + 5.5);
      doc.text("TELEFONE", 115, currentY + 5.5);
      doc.text("ORIGEM (CIDADE/UF)", 145, currentY + 5.5);
      doc.text("SITUAÇÃO", 182, currentY + 5.5, { align: 'right' });
      
      doc.setDrawColor(212, 212, 216); // Zinc-300
      doc.setLineWidth(0.3);
      doc.line(15, currentY + 8, 195, currentY + 8);
      currentY += 8;

      // Draw rows
      rClients.forEach((client, idx) => {
        if (idx % 2 === 1) {
          doc.setFillColor(250, 250, 250);
          doc.rect(15, currentY, 180, 8, 'F');
        }
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(63, 63, 70);
        
        // Trim names to fit
        const printableName = client.name.length > 32 ? client.name.slice(0, 30) + '...' : client.name;
        doc.text(printableName, 18, currentY + 5.5);
        doc.text(client.document || 'N/D', 75, currentY + 5.5);
        doc.text(client.phone || 'N/D', 115, currentY + 5.5);
        
        const cityState = client.city ? `${client.city}/${client.state.slice(0, 2).toUpperCase()}` : 'Não cadastrado';
        const printableCity = cityState.length > 20 ? cityState.slice(0, 18) + '..' : cityState;
        doc.text(printableCity, 145, currentY + 5.5);
        
        if (client.active) {
          doc.setTextColor(34, 197, 94); // Green font for active status
          doc.text("ATIVO", 182, currentY + 5.5, { align: 'right' });
        } else {
          doc.setTextColor(239, 68, 68); // Red font for inactive status
          doc.text("INATIVO", 182, currentY + 5.5, { align: 'right' });
        }
        
        doc.setDrawColor(244, 244, 245);
        doc.line(15, currentY + 8, 195, currentY + 8);
        currentY += 8;
        
        // Auto page break
        if (currentY > 265 && idx < rClients.length - 1) {
          doc.addPage();
          currentY = 20;
          
          doc.setFillColor(244, 244, 245);
          doc.rect(15, currentY, 180, 8, 'F');
          doc.setFont("helvetica", "bold");
          doc.setTextColor(39, 39, 42);
          doc.text("NOME DO HÓSPEDE (CONT.)", 18, currentY + 5.5);
          doc.text("CPF / DOCUMENTO", 75, currentY + 5.5);
          doc.text("TELEFONE", 115, currentY + 5.5);
          doc.text("ORIGEM (CIDADE/UF)", 145, currentY + 5.5);
          doc.text("SITUAÇÃO", 182, currentY + 5.5, { align: 'right' });
          currentY += 8;
        }
      });
      
      currentY += 10;
      
      // Bottom Verification
      if (currentY > 260) {
        doc.addPage();
        currentY = 20;
      }
      
      doc.setDrawColor(228, 228, 231); // Zinc-200
      doc.rect(15, currentY, 180, 15, 'D');
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(113, 113, 122);
      const activeCount = rClients.filter(c => c.active).length;
      const inactiveCount = rClients.filter(c => !c.active).length;
      doc.text(`CONTABILIZAÇÃO JURÍDICA: ${activeCount} ATIVOS | ${inactiveCount} INATIVOS`, 20, currentY + 9);
      
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(161, 161, 170);
        doc.text(`Relatório emitido via Painel de Auditoria de Clientes oficial - ${profile.name}.`, 15, 287);
        doc.text(`Página ${i} de ${pageCount}`, 195, 287, { align: 'right' });
      }

      doc.save(`relatorio_clientes_${new Date().toISOString().split('T')[0]}.pdf`);
      setShowReportModal(false);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("Erro ao gerar o documento PDF de clientes.");
    }
  };

  return (
    <div className="space-y-6">
      {/* Title section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
        <div>
          <h1 id="header-title-clients" className="text-xl font-semibold tracking-tight text-zinc-950">
            Controle de Clientes & Hóspedes
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Cadastre os hóspedes da pousada para viabilizar reservas, registrar consumos e imprimir notas fiscais de estadias.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            id="btn-trigger-report-modal"
            onClick={() => {
              setReportAllTime(true);
              setReportStatus('todos');
              setShowReportModal(true);
            }}
            className="flex items-center gap-1.5 px-3.5 py-2 border border-zinc-200 hover:border-zinc-300 rounded-lg text-zinc-700 bg-white font-semibold text-xs transition-all cursor-pointer shadow-sm"
          >
            <FileDown className="w-3.5 h-3.5 text-zinc-500" /> Relatório de Clientes
          </button>
          
          <button
            type="button"
            id="btn-add-client-trigger"
            onClick={openAddModal}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer shadow-sm active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" /> Adicionar Cliente
          </button>
        </div>
      </div>

      {/* Filter and search bar */}
      <div className="bg-white p-4 border border-zinc-200 rounded-xl shadow-sm flex flex-col md:flex-row items-center gap-4">
        {/* Search */}
        <div className="relative w-full md:flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-400">
            <Search className="w-4 h-4" />
          </div>
          <input
            type="text"
            id="search-clients-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-9 pr-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-colors"
            placeholder="Buscar por nome, CPF, telefone ou e-mail..."
          />
        </div>

        {/* Status Filter buttons */}
        <div className="flex items-center gap-2 self-stretch md:self-auto overflow-x-auto pb-1 md:pb-0">
          <span className="text-xxs font-bold text-zinc-400 uppercase tracking-wider mr-2 hidden md:inline">Filtro:</span>
          <button
            type="button"
            id="filter-cl-todos"
            onClick={() => setStatusFilter('todos')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
              statusFilter === 'todos' 
                ? 'bg-zinc-900 text-white shadow-sm' 
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            Todos ({clients.length})
          </button>
          <button
            type="button"
            id="filter-cl-ativos"
            onClick={() => setStatusFilter('ativos')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
              statusFilter === 'ativos' 
                ? 'bg-zinc-900 text-white shadow-sm' 
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            Ativos ({clients.filter(c => c.active).length})
          </button>
          <button
            type="button"
            id="filter-cl-inativos"
            onClick={() => setStatusFilter('inativos')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
              statusFilter === 'inativos' 
                ? 'bg-zinc-900 text-white shadow-sm' 
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            Inativos ({clients.filter(c => !c.active).length})
          </button>
        </div>
      </div>

      {/* Big Table Container */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table id="table-clients" className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                <th className="py-3 px-4 w-12">#</th>
                <th className="py-3 px-4">Nome completo</th>
                <th className="py-3 px-4">CPF / Documento</th>
                <th className="py-3 px-4">Data de Nascimento</th>
                <th className="py-3 px-4">Contato (E-mail / Fone)</th>
                <th className="py-3 px-4">Cidade / Estado</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-right w-24">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/60 font-medium text-zinc-700">
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={8} id="clients-empty-row" className="p-8 text-center text-zinc-400 font-normal">
                    Nenhum cliente correspondente encontrado nesta pousada.
                  </td>
                </tr>
              ) : (
                filteredClients.map((client, idx) => (
                  <tr key={client.id} id={`client-row-${client.id}`} className="hover:bg-zinc-50/50 transition-colors">
                    <td className="py-3.5 px-4 font-mono text-zinc-400 text-xxs">{(idx + 1).toString().padStart(2, '0')}</td>
                    <td className="py-3.5 px-4">
                      <div className="font-semibold text-zinc-900 text-xs">{client.name}</div>
                      <div className="text-xxs text-zinc-400 mt-0.5">ID: {client.id}</div>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-xs">{client.document || 'N/A'}</td>
                    <td className="py-3.5 px-4">{formatLocalDate(client.birthDate) || 'Não informado'}</td>
                    <td className="py-3.5 px-4">
                      <div className="text-xs">{client.phone}</div>
                      <div className="text-xxs text-zinc-400 lowcase">{client.email}</div>
                    </td>
                    <td className="py-3.5 px-4 text-xs font-normal">
                      {client.city ? `${client.city} - ${client.state}` : <span className="text-zinc-400">(Não informado)</span>}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="flex justify-center">
                        {client.active ? (
                          <span id={`client-status-badge-${client.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-200">
                            Ativo
                          </span>
                        ) : (
                          <span id={`client-status-badge-${client.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xxs font-bold uppercase tracking-wider bg-red-50 text-red-700 border border-red-200">
                            Inativo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          id={`btn-edit-client-${client.id}`}
                          title="Editar cadastro de cliente"
                          onClick={() => openEditModal(client)}
                          className="p-1 text-zinc-600 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-300 rounded cursor-pointer transition-colors bg-white hover:bg-zinc-50"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        
                        <button
                          type="button"
                          id={`btn-toggle-client-${client.id}`}
                          title={client.active ? "Desativar Cliente" : "Ativar Cliente"}
                          onClick={() => {
                            if (!isAdmin) {
                              alert("Permissão negada. Apenas Administradores podem ativar/desativar cadastros no sistema.");
                              return;
                            }
                            onUpdateClient(client.id, { active: !client.active });
                          }}
                          className={`p-1 rounded border transition-colors cursor-pointer ${
                            client.active 
                              ? 'text-green-600 hover:text-green-800 border-green-200 hover:border-green-300 bg-green-50/20' 
                              : 'text-red-600 hover:text-red-800 border-red-200 hover:border-red-300 bg-red-50/20'
                          }`}
                        >
                          {client.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card List View */}
        <div className="md:hidden divide-y divide-zinc-200">
          {filteredClients.length === 0 ? (
            <div id="clients-empty-mobile" className="p-8 text-center text-zinc-400 font-normal text-xs bg-white">
              Nenhum cliente correspondente encontrado nesta pousada.
            </div>
          ) : (
            filteredClients.map((client, idx) => (
              <div key={client.id} id={`client-card-mobile-${client.id}`} className="p-4 bg-white space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-zinc-900 text-sm">{client.name}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">ID: {client.id} | Doc: {client.document || 'N/A'}</div>
                  </div>
                  <span className="text-xxs font-mono text-zinc-400">#{idx + 1}</span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-650 bg-zinc-50 p-2.5 rounded-lg border border-zinc-150">
                  <div>
                    <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-0.5">Contato</span>
                    <div className="font-semibold text-zinc-800">{client.phone}</div>
                    <div className="text-zinc-500 font-normal truncate max-w-[120px]">{client.email}</div>
                  </div>
                  <div>
                    <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-0.5">Procedência</span>
                    <div className="font-semibold text-zinc-800">
                      {client.city ? `${client.city} - ${client.state}` : 'Não informado'}
                    </div>
                    <div className="text-zinc-500 font-normal">
                      Nasc.: {client.birthDate ? formatLocalDate(client.birthDate) : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-zinc-100 pt-2.5">
                  <div>
                    {client.active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-green-50 text-green-700 border border-green-200">
                        Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-50 text-red-700 border border-red-200">
                        Inativo
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      id={`btn-mobile-edit-client-${client.id}`}
                      onClick={() => openEditModal(client)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-lg text-zinc-700 bg-white text-xxs font-bold uppercase cursor-pointer hover:bg-zinc-50"
                    >
                      <Edit className="w-3.5 h-3.5 text-zinc-500" />
                      <span>Editar</span>
                    </button>
                    
                    <button
                      type="button"
                      id={`btn-mobile-toggle-client-${client.id}`}
                      onClick={() => {
                        if (!isAdmin) {
                          alert("Permissão negada. Apenas Administradores podem ativar/desativar cadastros no sistema.");
                          return;
                        }
                        onUpdateClient(client.id, { active: !client.active });
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xxs font-bold uppercase transition-colors cursor-pointer ${
                        client.active 
                          ? 'text-zinc-600 hover:text-green-800 border-zinc-200 bg-zinc-50' 
                          : 'text-red-600 hover:text-red-800 border-red-200 bg-red-50'
                      }`}
                    >
                      {client.active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                      <span>{client.active ? 'Ativo' : 'Inativo'}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

      </div>

      {/* Modal ADD CLIENT */}
      {showAddModal && (
        <div id="add-client-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-lg lg:max-w-2xl xl:max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Novo Cadastro de Hóspede</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAdd} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label htmlFor="form-cl-name" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Nome completo *</label>
                <input
                  type="text"
                  id="form-cl-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                  placeholder="Ex: Finn Cleyde"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-cl-birth" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Data de Nascimento</label>
                  <input
                    type="date"
                    id="form-cl-birth"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Ex: 29/08/2000"
                  />
                </div>
                <div>
                  <label htmlFor="form-cl-doc" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">CPF</label>
                  <input
                    type="text"
                    id="form-cl-doc"
                    value={document}
                    onChange={(e) => setDocument(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Ex: 000.000.000-00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-cl-phone" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Telefone</label>
                  <input
                    type="text"
                    id="form-cl-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Ex: (00) 0000-0000"
                  />
                </div>
                <div>
                  <label htmlFor="form-cl-email" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Email</label>
                  <input
                    type="email"
                    id="form-cl-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Ex: finncleyde@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-cl-state" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Estado</label>
                  <select
                    id="form-cl-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  >
                    <option value="Rio de Janeiro">Rio de Janeiro</option>
                    <option value="São Paulo">São Paulo</option>
                    <option value="Minas Gerais">Minas Gerais</option>
                    <option value="Espírito Santo">Espírito Santo</option>
                    <option value="Bahia">Bahia</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="form-cl-city" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Cidade</label>
                  <input
                    type="text"
                    id="form-cl-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                    placeholder="Ex: Duque de Caxias"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  id="btn-submit-client-add"
                  className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm"
                >
                  Cadastrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal EDIT CLIENT */}
      {showEditModal && selectedClient && (
        <div id="edit-client-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-lg lg:max-w-2xl xl:max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Edit className="w-4 h-4 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Editar Cadastro: {selectedClient.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEdit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label htmlFor="form-cl-edit-name" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Nome completo *</label>
                <input
                  type="text"
                  id="form-cl-edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none focus:ring-1 focus:ring-zinc-900"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-cl-edit-birth" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Data de Nascimento</label>
                  <input
                    type="date"
                    id="form-cl-edit-birth"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="form-cl-edit-doc" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">CPF</label>
                  <input
                    type="text"
                    id="form-cl-edit-doc"
                    value={document}
                    onChange={(e) => setDocument(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-cl-edit-phone" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Telefone</label>
                  <input
                    type="text"
                    id="form-cl-edit-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="form-cl-edit-email" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Email</label>
                  <input
                    type="email"
                    id="form-cl-edit-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="form-cl-edit-state" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Estado</label>
                  <select
                    id="form-cl-edit-state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  >
                    <option value="Rio de Janeiro">Rio de Janeiro</option>
                    <option value="São Paulo">São Paulo</option>
                    <option value="Minas Gerais">Minas Gerais</option>
                    <option value="Espírito Santo">Espírito Santo</option>
                    <option value="Bahia">Bahia</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="form-cl-edit-city" className="block text-xxs font-bold text-zinc-500 uppercase tracking-wide mb-1">Cidade</label>
                  <input
                    type="text"
                    id="form-cl-edit-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 bg-zinc-50/30 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  id="btn-submit-client-edit"
                  className="px-5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm"
                >
                  Editar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal REPORT FILTER */}
      {showReportModal && (
        <div id="report-modal" className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white border border-zinc-200 shadow-xl rounded-xl max-w-sm lg:max-w-lg w-full max-h-[92vh] flex flex-col overflow-hidden transform transition-all my-auto">
            <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <FileDown className="w-5 h-5 text-zinc-300" />
                <h3 className="text-sm font-bold uppercase tracking-wider">Relatório de Clientes</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowReportModal(false)}
                className="text-zinc-400 hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer">
                  <input
                    type="checkbox"
                    id="checkbox-report-all-time"
                    checked={reportAllTime}
                    onChange={(e) => setReportAllTime(e.target.checked)}
                    className="w-4 h-4 text-zinc-900 rounded focus:ring-0 border-zinc-300"
                  />
                  <span>Todo período</span>
                </label>
              </div>

              {!reportAllTime && (
                <div id="report-date-range-inputs" className="space-y-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <span className="block text-xxs font-bold text-zinc-400 uppercase tracking-wider">Definir Filtro Range:</span>
                  <div>
                    <label htmlFor="report-input-start" className="block text-xxs font-bold text-zinc-500 mb-0.5">De:</label>
                    <input
                      type="date"
                      id="report-input-start"
                      value={reportStart}
                      onChange={(e) => setReportStart(e.target.value)}
                      className="w-full text-xs p-1.5 border border-zinc-200 rounded-md text-zinc-800 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label htmlFor="report-input-end" className="block text-xxs font-bold text-zinc-500 mb-0.5">Até:</label>
                    <input
                      type="date"
                      id="report-input-end"
                      value={reportEnd}
                      onChange={(e) => setReportEnd(e.target.value)}
                      className="w-full text-xs p-1.5 border border-zinc-200 rounded-md text-zinc-800 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xxs font-bold text-zinc-400 uppercase tracking-wider mb-2">Filtrar por Situação:</label>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer">
                    <input
                      type="radio"
                      name="report-status-radio"
                      checked={reportStatus === 'todos'}
                      onChange={() => setReportStatus('todos')}
                      className="w-4 h-4 text-zinc-900"
                    />
                    <span>Todos</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer">
                    <input
                      type="radio"
                      name="report-status-radio"
                      checked={reportStatus === 'ativos'}
                      onChange={() => setReportStatus('ativos')}
                      className="w-4 h-4 text-zinc-900"
                    />
                    <span>Ativos</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 cursor-pointer">
                    <input
                      type="radio"
                      name="report-status-radio"
                      checked={reportStatus === 'inativos'}
                      onChange={() => setReportStatus('inativos')}
                      className="w-4 h-4 text-zinc-900"
                    />
                    <span>Inativos</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-zinc-700 font-semibold text-xs cursor-pointer"
                >
                  Fechar
                </button>
                <button
                  type="button"
                  id="btn-report-generate-pdf"
                  onClick={generatePDFReport}
                  className="px-5 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer shadow-sm"
                >
                  Gerar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
